import { useState } from 'react'
import { Pencil, Trash2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useWasteSubcategories,
  useCreateWasteSubcategory,
  useUpdateWasteSubcategory,
  useDeleteWasteSubcategory,
} from '@/hooks/queries/useWasteSubcategories'
import { WasteTypesToolbar } from '@/pages/waste-types/components/WasteTypesToolbar'
import { MultiSelect } from '@/components/ui/multi-select'
import type { WasteCategory, WasteSubcategory } from '@/types'

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; subcategory: WasteSubcategory }
  | { mode: 'delete'; subcategory: WasteSubcategory }

function getServerError(error: unknown): string | null {
  if (!error) return null
  const err = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
  const errors = err.response?.data?.errors
  if (errors) return Object.values(errors).flat().join(' ')
  return err.response?.data?.message ?? 'Ocurrió un error inesperado'
}

interface Props {
  categories: WasteCategory[]
}

export function WasteSubcategoriesTab({ categories }: Props) {
  const { data: subcategories, isLoading } = useWasteSubcategories()
  const createMutation = useCreateWasteSubcategory()
  const updateMutation = useUpdateWasteSubcategory()
  const deleteMutation = useDeleteWasteSubcategory()

  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' })
  const [nameInput, setNameInput] = useState('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [search, setSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const openCreate = () => {
    setNameInput('')
    setCategoryId('')
    createMutation.reset()
    setDialog({ mode: 'create' })
  }

  const openEdit = (subcategory: WasteSubcategory) => {
    setNameInput(subcategory.name)
    setCategoryId(String(subcategory.waste_category_id))
    updateMutation.reset()
    setDialog({ mode: 'edit', subcategory })
  }

  const openDelete = (subcategory: WasteSubcategory) => {
    deleteMutation.reset()
    setDialog({ mode: 'delete', subcategory })
  }

  const closeDialog = () => setDialog({ mode: 'closed' })

  const handleSubmit = async () => {
    if (!categoryId) return
    if (dialog.mode === 'create') {
      await createMutation.mutateAsync({
        waste_category_id: Number(categoryId),
        name: nameInput,
      })
      closeDialog()
    } else if (dialog.mode === 'edit') {
      await updateMutation.mutateAsync({
        id: dialog.subcategory.id,
        data: { waste_category_id: Number(categoryId), name: nameInput },
      })
      closeDialog()
    }
  }

  const handleDelete = async () => {
    if (dialog.mode !== 'delete') return
    await deleteMutation.mutateAsync(dialog.subcategory.id)
    closeDialog()
  }

  const isFormDialog = dialog.mode === 'create' || dialog.mode === 'edit'
  const activeMutation = dialog.mode === 'create' ? createMutation : updateMutation
  const formError = isFormDialog ? getServerError(activeMutation.error) : null
  const deleteError = dialog.mode === 'delete' ? getServerError(deleteMutation.error) : null

  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }))

  const normalizedSearch = search.trim().toLowerCase()
  const filtered = (subcategories ?? []).filter((s) => {
    if (selectedCategories.length > 0 && !selectedCategories.includes(String(s.waste_category_id))) return false
    if (normalizedSearch && !s.name.toLowerCase().includes(normalizedSearch) && !s.category?.name.toLowerCase().includes(normalizedSearch)) return false
    return true
  })

  return (
    <>
      <CardHeader className="space-y-4">
        <div className="flex items-center justify-between">
          <WasteTypesToolbar
            search={search}
            onSearchChange={setSearch}
            onClearFilters={() => { setSearch(''); setSelectedCategories([]) }}
            hasActiveFilters={Boolean(search.trim()) || selectedCategories.length > 0}
          >
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Categorías"
            />
          </WasteTypesToolbar>
          <Button onClick={openCreate} className="ml-4 shrink-0">
            <Plus className="mr-2 h-4 w-4" />
            Nueva subcategoría
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : !subcategories?.length ? (
          <p className="text-sm text-muted-foreground">No hay subcategorías registradas.</p>
        ) : !filtered.length ? (
          <p className="text-sm text-muted-foreground">No se encontraron subcategorías para esa búsqueda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                <th className="pb-2">Categoría</th>
                <th className="pb-2">Subcategoría</th>
                <th className="pb-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub) => (
                <tr key={sub.id} className="border-b last:border-0">
                  <td className="py-3 text-muted-foreground">{sub.category?.name ?? '—'}</td>
                  <td className="py-3 font-medium text-foreground">{sub.name}</td>
                  <td className="py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mr-1 h-8 w-8"
                      onClick={() => openEdit(sub)}
                      aria-label={`Editar ${sub.name}`}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openDelete(sub)}
                      aria-label={`Eliminar ${sub.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={isFormDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'create' ? 'Nueva subcategoría' : 'Editar subcategoría'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="subcategory-category">Categoría</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger id="subcategory-category">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="subcategory-name">Nombre</Label>
              <Input
                id="subcategory-name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                autoFocus
              />
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!nameInput.trim() || !categoryId || activeMutation.isPending}
            >
              {activeMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialog.mode === 'delete'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar subcategoría</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70">
            ¿Estás seguro que deseas eliminar{' '}
            <span className="font-semibold">
              {dialog.mode === 'delete' ? dialog.subcategory.name : ''}
            </span>
            ? Esta acción no se puede deshacer.
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
